import React, { useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell
} from 'recharts';

const GexDeltaChart = ({ data, spotPrice, interval, pctChange, totalGexDelta }) => {
  // Transform data for horizontal bar chart
  // Filter to show strikes within reasonable range of spot price
  const chartData = useMemo(() => {
    if (!data || !spotPrice) return [];

    const range = spotPrice * 0.05; // 5% range
    return data
      .filter(item => item.strike >= spotPrice - range && item.strike <= spotPrice + range)
      .map(item => ({
        strike: item.strike,
        gex: item.gexDelta / 1e6, // Convert to millions
        isPositive: item.gexDelta >= 0
      }));
  }, [data, spotPrice]);

  // Calculate the max absolute value for symmetric axis (from delta data itself)
  const maxAbsGex = useMemo(() => {
    if (!chartData.length) return 1;
    const max = Math.max(...chartData.map(d => Math.abs(d.gex)));
    return max > 0 ? max * 1.1 : 1;
  }, [chartData]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    return (
      <div style={styles.tooltip}>
        <div style={styles.tooltipTitle}>Strike: {data.strike}</div>
        <div style={{ color: data.gex >= 0 ? '#00ff88' : '#ff4444' }}>
          GEX Delta: {data.gex >= 0 ? '+' : ''}{data.gex.toFixed(2)}M
        </div>
        <div style={{ color: '#8b949e', fontSize: '11px', marginTop: '4px' }}>
          {data.gex >= 0 ? 'Gamma support increased' : 'Gamma support decreased'}
        </div>
      </div>
    );
  };

  if (!data || !chartData.length) {
    return (
      <div style={styles.noData}>
        <h2 style={styles.chartTitle}>GEX Delta ({interval || '--'})</h2>
        <p style={styles.waitingText}>Waiting for next refresh to calculate change...</p>
        <p style={styles.hintText}>Delta will show after the first data refresh</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.chartTitle}>
        GEX Delta ({interval})
        <span style={{
          marginLeft: '12px',
          fontSize: '14px',
          color: pctChange >= 0 ? '#00ff88' : '#ff4444'
        }}>
          {pctChange !== undefined ? `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}%` : ''}
          {totalGexDelta !== undefined ? ` (${totalGexDelta >= 0 ? '+' : ''}${(totalGexDelta / 1e9).toFixed(1)}B)` : ''}
        </span>
      </h2>
      <div style={styles.legend}>
        <span style={{ ...styles.legendItem, color: '#00ff88' }}>Increased GEX</span>
        <span style={{ ...styles.legendItem, color: '#ff4444' }}>Decreased GEX</span>
      </div>
      <ResponsiveContainer width="100%" height={600}>
        <ComposedChart
          layout="vertical"
          data={chartData}
          margin={{ top: 20, right: 30, left: 60, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#30363d" horizontal={true} vertical={true} />
          <XAxis
            type="number"
            domain={[-maxAbsGex, maxAbsGex]}
            tick={{ fill: '#8b949e', fontSize: 12 }}
            tickFormatter={(value) => `${value >= 0 ? '+' : ''}${value.toFixed(0)}M`}
            axisLine={{ stroke: '#30363d' }}
          />
          <YAxis
            type="category"
            dataKey="strike"
            tick={{ fill: '#8b949e', fontSize: 11 }}
            width={55}
            axisLine={{ stroke: '#30363d' }}
            tickFormatter={(value) => value.toLocaleString()}
            reversed={true}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#58a6ff" strokeWidth={1} />
          {spotPrice && (
            <ReferenceLine
              y={chartData.find(d => Math.abs(d.strike - spotPrice) ===
                Math.min(...chartData.map(c => Math.abs(c.strike - spotPrice))))?.strike}
              stroke="#ffcc00"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: 'spot',
                fill: '#ffcc00',
                fontSize: 10,
                position: 'right'
              }}
            />
          )}
          <Bar dataKey="gex" name="GEX Delta" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPositive ? '#00ff88' : '#ff4444'}
                fillOpacity={0.8}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      <div style={styles.footer}>
        <p style={styles.footerText}>
          Shows change in GEX since last refresh. Green = GEX increased, Red = GEX decreased.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d',
    padding: '24px'
  },
  chartTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#ffffff'
  },
  legend: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    fontSize: '12px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tooltip: {
    backgroundColor: '#1c2128',
    border: '1px solid #30363d',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    lineHeight: 1.6
  },
  tooltipTitle: {
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '4px'
  },
  footer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #30363d'
  },
  footerText: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: 1.5
  },
  noData: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#8b949e',
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d',
    minHeight: '600px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  waitingText: {
    fontSize: '16px',
    marginTop: '16px',
    color: '#e6e6e6'
  },
  hintText: {
    fontSize: '14px',
    marginTop: '8px',
    color: '#8b949e'
  }
};

export default GexDeltaChart;
